"use client"

import { useEffect, useState } from "react"
import { useRouter, usePathname } from "next/navigation"
import { authService } from "@/services/auth"
import { DashboardSidebar } from "@/components/dashboard/sidebar"
import { DashboardHeader } from "@/components/dashboard/header"
import { Loader2, MessageSquareWarning, Bug } from "lucide-react"
import { PageLoader } from "@/components/ui/page-loader"
import { ReportIssueModal } from "@/components/dashboard/shared/report-issue-modal"
import { Button } from "@/components/ui/button"
import { NotificationProvider } from "@/contexts/NotificationContext"
import { MaintenanceBanner } from "@/components/maintenance-banner"
import { SidebarModeProvider } from "@/components/providers/sidebar-mode-provider"
// import { CouponBanner } from "@/components/landing/coupon-banner"

export default function DashboardLayout({
    children,
}: {
    children: React.ReactNode
}) {
    const router = useRouter()
    const pathname = usePathname()
    const [isLoading, setIsLoading] = useState(true)
    const [isCollapsed, setIsCollapsed] = useState(false)
    const [isReportModalOpen, setIsReportModalOpen] = useState(false)
    const [token, setToken] = useState<string | undefined>(undefined)

    useEffect(() => {
        const storedToken = authService.getToken()
        console.log('[LAYOUT] Auth check. Cookie token:', storedToken ? `FOUND (${storedToken.length} chars)` : 'NOT FOUND');
        console.log('[LAYOUT] document.cookie:', typeof document !== 'undefined' ? (document.cookie ? document.cookie.substring(0, 200) : '(empty)') : 'SSR');
        if (!storedToken) {
            const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);
            router.replace(`/login?next=${currentUrl}`)
        } else {
            setToken(storedToken)
            setIsLoading(false)
        }

        const handleOpenReport = () => setIsReportModalOpen(true)
        window.addEventListener('open-report-issue', handleOpenReport)
        return () => window.removeEventListener('open-report-issue', handleOpenReport)
    }, [router])

    if (isLoading) {
        return <PageLoader className="fixed inset-0 bg-background" />
    }

    const isImmersivePage = pathname?.startsWith("/dashboard/practice/session") ||
        pathname?.startsWith("/dashboard/quiz/session") ||
        pathname?.startsWith("/dashboard/reading/continue") ||
        pathname?.startsWith("/dashboard/reading/practice") ||
        (pathname?.startsWith("/dashboard/wall") && !pathname?.startsWith("/dashboard/wallet")) ||
        pathname?.startsWith("/dashboard/courses/") ||
        pathname?.startsWith("/dashboard/ai-chat");

    // Safe to render Provider even if token is undefined (it handles it)
    return (
        <SidebarModeProvider>
        <NotificationProvider token={token}>
            {isImmersivePage ? (
                children
            ) : (
                <div className="fixed inset-0 bg-background flex overflow-hidden">
                    {/* First thing in the tab order. The sidebar carries up to ~40
                        links, so without this a keyboard user traverses the whole
                        rail again on every single page. */}
                    <a
                        href="#main-content"
                        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
                    >
                        Skip to main content
                    </a>
                    <DashboardSidebar collapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
                    <div
                        className={`flex flex-col h-full transition-all duration-300 w-full ${isCollapsed ? "md:ml-20 md:w-[calc(100%-5rem)]" : "md:ml-64 md:w-[calc(100%-16rem)]"
                            }`}
                    >
                        <DashboardHeader />
                        {/* <CouponBanner /> */}
                        <MaintenanceBanner />
                        <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto flex flex-col p-3 sm:p-4 md:p-6 outline-none">
                            {children}
                        </main>
                    </div>

                    <ReportIssueModal
                        isOpen={isReportModalOpen}
                        onClose={() => setIsReportModalOpen(false)}
                    />

                    {/* Floating Report Bug Button - Home Only with Glossy Effect */}
                    {pathname === "/dashboard" && (
                        <div className="fixed bottom-6 right-6 z-40 group">
                            {/* Glossy Instruction Tooltip */}
                            <div aria-hidden="true" className="absolute bottom-full right-0 mb-4 opacity-0 group-hover:opacity-100 transition-all duration-300 motion-safe:translate-y-2 motion-safe:group-hover:translate-y-0 pointer-events-none">
                                <div className="relative bg-white/10 dark:bg-black/20 backdrop-blur-md border border-white/20 dark:border-white/10 px-4 py-2 rounded-2xl shadow-xl overflow-hidden min-w-[180px]">
                                    {/* Glass Shine Effect */}
                                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />

                                    <div className="relative">
                                        <p className="text-sm font-bold text-foreground mb-0.5">Found a bug?</p>
                                        <p className="text-sm text-muted-foreground font-medium">Click to report and help us improve!</p>
                                    </div>

                                    {/* Tip of the tooltip */}
                                    <div className="absolute top-full right-6 w-3 h-3 bg-white/10 dark:bg-black/20 backdrop-blur-md border-r border-b border-white/20 dark:border-white/10 rotate-45 -mt-1.5" />
                                </div>
                            </div>

                            <button
                                onClick={() => setIsReportModalOpen(true)}
                                className="relative p-4 rounded-full bg-rose-500 text-white shadow-lg shadow-rose-500/30 hover:bg-rose-600 transition-all duration-200 motion-safe:hover:scale-110 motion-safe:active:scale-95 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                                aria-label="Report a bug"
                            >
                                <Bug className="h-6 w-6" aria-hidden="true" />

                                {/* Attention pulse — decorative, so it is dropped entirely
                                    when the reader has asked for reduced motion. */}
                                <span
                                    aria-hidden="true"
                                    className="absolute inset-0 rounded-full bg-rose-500 opacity-0 motion-safe:animate-ping motion-safe:opacity-20 motion-safe:group-hover:opacity-0"
                                />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </NotificationProvider>
        </SidebarModeProvider>
    )
}
